import type { CreateTeamMemberData, TeamMember, TeamMemberFilters, UpdateTeamMemberData } from "@/types/team";

// Mock data per team members
const mockTeamMembers: TeamMember[] = [
    // Team per TechCorp Solutions LLC (senderId: "1")
    {
        id: "1",
        accountId: "1",
        name: "John Smith",
        email: "john.smith@techcorp.com",
        role: "Admin",
        status: "Active",
        invitedAt: new Date("2024-01-01"),
        lastLoginAt: new Date("2024-02-01"),
    },
    {
        id: "2",
        accountId: "1",
        name: "Sarah Johnson",
        email: "sarah.johnson@techcorp.com",
        role: "Editor",
        status: "Active",
        invitedAt: new Date("2024-01-05"),
        lastLoginAt: new Date("2024-01-30"),
    },
    {
        id: "3",
        accountId: "1",
        name: "Mike Wilson",
        email: "mike.wilson@techcorp.com",
        role: "Viewer",
        status: "Active",
        invitedAt: new Date("2024-01-10"),
        lastLoginAt: new Date("2024-01-28"),
    },
    {
        id: "4",
        accountId: "1",
        name: "Lisa Brown",
        email: "lisa.brown@techcorp.com",
        role: "Editor",
        status: "Inactive",
        invitedAt: new Date("2024-01-15"),
        lastLoginAt: new Date("2024-01-20"),
    },

    // Team per Innovation Hub Partnership (senderId: "3")
    {
        id: "5",
        accountId: "3",
        name: "Hans Mueller",
        email: "hans.mueller@innovationhub.de",
        role: "Admin",
        status: "Active",
        invitedAt: new Date("2024-01-02"),
        lastLoginAt: new Date("2024-02-01"),
    },
    {
        id: "6",
        accountId: "3",
        name: "Anna Schmidt",
        email: "anna.schmidt@innovationhub.de",
        role: "Editor",
        status: "Active",
        invitedAt: new Date("2024-01-08"),
        lastLoginAt: new Date("2024-01-29"),
    },
    {
        id: "7",
        accountId: "3",
        name: "Thomas Weber",
        email: "thomas.weber@innovationhub.de",
        role: "Viewer",
        status: "Active",
        invitedAt: new Date("2024-01-12"),
        lastLoginAt: new Date("2024-01-25"),
    },

    // Team per Sole Proprietor Consulting (senderId: "7")
    {
        id: "8",
        accountId: "7",
        name: "John Doe",
        email: "john@consulting.ca",
        role: "Admin",
        status: "Active",
        invitedAt: new Date("2024-01-14"),
        lastLoginAt: new Date("2024-02-01"),
    },
    {
        id: "9",
        accountId: "7",
        name: "Jane Smith",
        email: "jane.smith@consulting.ca",
        role: "Editor",
        status: "Active",
        invitedAt: new Date("2024-01-20"),
        lastLoginAt: new Date("2024-01-31"),
    },
    // Team per Global Logistics Forwarding (accountId: "ff-1")
    {
        id: "10",
        accountId: "ff-1",
        name: "Giovanni Bianchi",
        email: "giovanni.bianchi@globallogistics.it",
        role: "Admin",
        status: "Active",
        invitedAt: new Date("2024-01-18"),
        lastLoginAt: new Date("2024-02-04"),
    },
    {
        id: "11",
        accountId: "ff-1",
        name: "Martina Conti",
        email: "martina.conti@globallogistics.it",
        role: "Editor",
        status: "Inactive",
        invitedAt: new Date("2024-01-22"),
        lastLoginAt: new Date("2024-01-28"),
    },
];

// In-memory storage
let teamMembers = [...mockTeamMembers];

// Helper functions per CRUD operations
export const teamService = {
    // Get all team members
    getAll: (): TeamMember[] => {
        return [...teamMembers];
    },

    // Get team member by ID
    getById: (id: string): TeamMember | undefined => {
        return teamMembers.find((member) => member.id === id);
    },

    // Get team members by account ID
    getByAccountId: (accountId: string): TeamMember[] => {
        return teamMembers.filter((member) => member.accountId === accountId);
    },

    // Create new team member
    create: (data: CreateTeamMemberData): TeamMember => {
        const newMember: TeamMember = {
            id: (teamMembers.length + 1).toString(),
            ...data,
            invitedAt: new Date(),
        };
        teamMembers.push(newMember);
        return newMember;
    },

    // Update team member
    update: (id: string, data: UpdateTeamMemberData): TeamMember | null => {
        const index = teamMembers.findIndex((member) => member.id === id);
        if (index === -1) return null;

        teamMembers[index] = {
            ...teamMembers[index],
            ...data,
        };
        return teamMembers[index];
    },

    // Delete team member
    delete: (id: string): boolean => {
        const index = teamMembers.findIndex((member) => member.id === id);
        if (index === -1) return false;

        teamMembers.splice(index, 1);
        return true;
    },

    // Search and filter team members
    search: (filters: TeamMemberFilters): TeamMember[] => {
        let filtered = [...teamMembers];

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter((member) => member.name.toLowerCase().includes(searchTerm) || member.email.toLowerCase().includes(searchTerm));
        }

        if (filters.role) {
            filtered = filtered.filter((member) => member.role === filters.role);
        }

        if (filters.status) {
            filtered = filtered.filter((member) => member.status === filters.status);
        }

        if (filters.accountId) {
            filtered = filtered.filter((member) => member.accountId === filters.accountId);
        }

        return filtered;
    },

    // Get team statistics
    getStats: (accountId?: string): { total: number; active: number; inactive: number; admins: number; editors: number; viewers: number } => {
        let filteredMembers = accountId ? teamMembers.filter((m) => m.accountId === accountId) : teamMembers;

        return {
            total: filteredMembers.length,
            active: filteredMembers.filter((member) => member.status === "Active").length,
            inactive: filteredMembers.filter((member) => member.status === "Inactive").length,
            admins: filteredMembers.filter((member) => member.role === "Admin").length,
            editors: filteredMembers.filter((member) => member.role === "Editor").length,
            viewers: filteredMembers.filter((member) => member.role === "Viewer").length,
        };
    },

    // Get role description
    getRoleDescription: (role: string): string => {
        const descriptions: Record<string, string> = {
            Admin: "Accesso completo: può gestire container, documenti, team e collaborazioni",
            Editor: "Può modificare container e documenti, ma non gestire team o collaborazioni",
            Viewer: "Accesso in sola lettura: può visualizzare ma non modificare",
        };
        return descriptions[role] || "Ruolo non definito";
    },
};
